import client from './client';
import {
  ExtractionEvidence,
  CorporateTransactionPreview,
  CorporateTransactionWriteResponse,
} from '../types/corporateTransaction';

export type DocumentSourceType = 'presentation' | 'press_release';
export type ExtractorName = 'milestones' | 'pipeline_intelligence' | 'corporate_transactions';

export interface DocumentIntelligenceDocument {
  content_id: string;
  company_id: string;
  company_name?: string | null;
  source_type: DocumentSourceType;
  title: string;
  published_date?: string | null;
  review_date: string;
  url?: string | null;
  ledger: Record<string, string | null | undefined>;
  last_corporate_transaction_pipeline_run_status?:
    | 'completed'
    | 'empty'
    | 'partial'
    | 'failed'
    | null;
  last_corporate_transaction_pipeline_run_date?: string | null;
  milestones: DocumentMilestoneSummary[];
  corporate_transactions: DocumentCorporateTransactionSummary[];
}

export interface DocumentMilestoneSummary {
  id?: string | null;
  milestone: string;
  date_label: string;
  status: string;
}

export interface DocumentCorporateTransactionSummary {
  id?: string | null;
  candidate_id?: string | null;
  origin: 'canonical' | 'review_candidate';
  transaction_class: string;
  transaction_type: string;
  status?: string | null;
}

export interface DocumentInventoryResponse {
  company_id: string | null;
  company_name?: string | null;
  days_back: number;
  review_as_of: string;
  documents_found: number;
  press_releases_found: number;
  presentations_found: number;
  has_more?: boolean;
  next_cursor_date?: string | null;
  next_cursor_title?: string | null;
  next_cursor_id?: string | null;
  next_cursor_company_id?: string | null;
  truncated_sources: string[];
  documents: DocumentIntelligenceDocument[];
}

export interface MilestoneFinding {
  id?: string | null;
  milestone: string;
  date_label: string;
  date_start?: string | null;
  date_end?: string | null;
  status: string;
  source_type?: string | null;
  product_id?: string | null;
  origin?: 'canonical' | 'live_extract';
}

export interface PipelineFinding {
  key: string;
  therapeutic_name: string;
  difference_type: string;
  reported_stage?: string | null;
  normalized_stage?: string | null;
  indication_text?: string | null;
  modality?: string | null;
  mechanism_of_action?: string | null;
  targets: string[];
  biological_target_stated: boolean;
  owner_name?: string | null;
  development_model: string;
  partner_names: string[];
  substantive_facts: string[];
  status: string;
  addable: boolean;
  evidence_quotes: string[];
  evidence: Array<{
    content_id: string;
    source_title?: string | null;
    evidence_text?: string | null;
    page_or_slide?: string | null;
    assertion_keys?: string[];
  }>;
  evidence_by_field: Record<
    string,
    Array<{
      content_id: string;
      source_title?: string | null;
      evidence_text?: string | null;
      page_or_slide?: string | null;
      assertion_keys?: string[];
    }>
  >;
}

export interface CorporateTransactionFinding {
  id?: string | null;
  candidate_id?: string | null;
  origin: 'canonical' | 'live_extract' | 'review_candidate';
  transaction_class: string;
  transaction_type: string;
  status?: string | null;
  announced_date?: string | null;
  parties: Array<Record<string, unknown>>;
  summary?: string | null;
  evidence_quotes: string[];
  evidence: ExtractionEvidence[];
  financial_terms: Array<Record<string, unknown>>;
  validation_errors: string[];
  candidate?: CorporateTransactionPreview | null;
}

export interface ExtractorSection {
  extractor: ExtractorName;
  status: string;
  count: number;
  message?: string | null;
  ledger_status?: string | null;
}

export interface DocumentFindingsResponse {
  company_id: string;
  content_id: string;
  source_type: DocumentSourceType;
  title?: string | null;
  published_date?: string | null;
  url?: string | null;
  sections: ExtractorSection[];
  milestones: MilestoneFinding[];
  pipeline: PipelineFinding[];
  corporate_transactions: CorporateTransactionFinding[];
}

export interface DocumentExtractRequest {
  source_type: DocumentSourceType;
  extractors?: ExtractorName[];
}

export interface BulkDocumentSelection {
  company_id: string;
  company_name?: string | null;
  content_id: string;
  source_type: DocumentSourceType;
  title?: string | null;
}

export interface BulkProcessingRequest {
  documents: BulkDocumentSelection[];
  analyses: readonly ExtractorName[];
  override_processed?: boolean;
  confirm_corporate_transaction_canonical_write: boolean;
}

export interface BatchCompanyCandidate {
  company_id: string;
  company_name: string;
  ticker?: string | null;
  corporate_status?: string | null;
  coverage_priority?: string | null;
  press_release_count: number;
  presentation_count: number;
  document_count: number;
}

export interface BatchCompanyInventoryResponse {
  offset: number;
  limit: number;
  has_more: boolean;
  matching_document_count: number;
  companies: BatchCompanyCandidate[];
}

export interface CompanyBulkProcessingRequest {
  company_ids: string[];
  select_all_matching: boolean;
  search: string;
  corporate_status: string;
  coverage_priority?: string;
  server_id?: string | null;
  channel_id?: string | null;
  days_back: number;
  analyses: readonly ExtractorName[];
  override_processed: boolean;
  confirm_corporate_transaction_canonical_write: boolean;
}

export interface BulkAnalysisOutcome {
  extractor: ExtractorName;
  status: 'completed' | 'empty' | 'partial' | 'failed' | 'skipped';
  item_count: number;
  saved_count: number;
  error?: string | null;
}

export interface BulkDocumentOutcome extends BulkDocumentSelection {
  status: 'completed' | 'partial' | 'failed';
  analyses: BulkAnalysisOutcome[];
}

export interface BulkCompanyResult {
  company_id: string;
  company_name: string;
  status: 'completed' | 'partial' | 'failed';
  documents_total: number;
  documents_completed: number;
  completed_at: string;
  analyses: BulkAnalysisOutcome[];
}

export interface BulkCompanySummary {
  company_id: string;
  company_name: string;
  documents_total: number;
}

export interface BulkProcessingRun {
  run_id: string;
  status: 'queued' | 'running' | 'completed' | 'partial' | 'failed';
  environment: string;
  performed_by: string;
  requested_analyses: ExtractorName[];
  documents_total: number;
  documents_completed: number;
  companies_total: number;
  companies_completed: number;
  company_names?: string[];
  company_summaries?: BulkCompanySummary[];
  lookback_days?: number | null;
  current_company_id?: string | null;
  current_company_name?: string | null;
  started_at: string;
  finished_at?: string | null;
  outcomes: BulkDocumentOutcome[];
  company_results?: BulkCompanyResult[];
}

export interface CompanyStateCorporateTransaction {
  id: string;
  transaction_class: string;
  transaction_type: string;
  status?: string | null;
  announced_date?: string | null;
  announced_date_basis?: 'explicit' | 'source_published' | 'legacy_unknown';
  first_observed_date?: string | null;
  last_observed_date?: string | null;
  summary?: string | null;
  source_content_ids?: string[];
  source_summaries?: string[];
  observed_transaction_types?: string[];
  observed_statuses?: string[];
  summary_source_content_ids?: string[];
  summary_needs_refresh?: boolean;
  summary_updated_at?: string | null;
  evidence_count?: number;
  parties: Array<Record<string, unknown>>;
  product_ids: string[];
  technology_ids: string[];
  disease_ids: string[];
}

export interface CompanyStateResponse {
  company_id: string;
  company_name?: string | null;
  milestones: MilestoneFinding[];
  pipeline: PipelineFinding[];
  corporate_transactions: CompanyStateCorporateTransaction[];
}

const BASE = '/api/v1/document-intelligence';

export const listDocuments = async (
  companyId: string | null | undefined,
  daysBack: number,
  pagination?: {
    limit?: number;
    afterDate?: string;
    afterTitle?: string;
    afterId?: string;
    afterCompanyId?: string;
    reviewAsOf?: string;
  }
): Promise<DocumentInventoryResponse> => {
  const path = companyId
    ? `${BASE}/companies/${encodeURIComponent(companyId)}/documents`
    : `${BASE}/documents`;
  const response = await client.get(path, {
    params: {
      days_back: daysBack,
      ...(!companyId && pagination?.limit != null ? { limit: pagination.limit } : {}),
      ...(!companyId && pagination?.afterDate
        ? {
            after_date: pagination.afterDate,
            after_title: pagination.afterTitle,
            after_id: pagination.afterId,
            after_company_id: pagination.afterCompanyId,
            review_as_of: pagination.reviewAsOf,
          }
        : {}),
    },
  });
  return response.data;
};

export const getDocumentFindings = async (
  companyId: string,
  contentId: string,
  sourceType: DocumentSourceType,
  opts?: { title?: string; url?: string | null }
): Promise<DocumentFindingsResponse> => {
  const response = await client.get(
    `${BASE}/companies/${encodeURIComponent(companyId)}/documents/${encodeURIComponent(contentId)}/findings`,
    {
      params: {
        source_type: sourceType,
        ...(opts?.title ? { title: opts.title } : {}),
        ...(opts?.url ? { url: opts.url } : {}),
      },
    }
  );
  return response.data;
};

export const extractDocumentFindings = async (
  companyId: string,
  contentId: string,
  sourceType: DocumentSourceType,
  extractors?: ExtractorName[]
): Promise<DocumentFindingsResponse> => {
  const response = await client.post(
    `${BASE}/companies/${encodeURIComponent(companyId)}/documents/${encodeURIComponent(contentId)}/extract`,
    {
      source_type: sourceType,
      extractors: extractors ?? ['milestones', 'pipeline_intelligence', 'corporate_transactions'],
    },
    // Milestone + Corporate Transaction LLM can exceed default axios timeout.
    { timeout: 600000 }
  );
  return response.data;
};

export const approveDocumentCorporateTransactionCandidate = async (
  companyId: string,
  sourceType: DocumentSourceType,
  transaction: CorporateTransactionPreview
): Promise<CorporateTransactionWriteResponse> => {
  const response = await client.post('/api/v1/corporate-transactions/candidates/approved', {
    company_id: companyId,
    source_type: sourceType,
    transaction,
  });
  return response.data;
};

export const startBulkProcessing = async (
  request: BulkProcessingRequest,
  idempotencyKey: string
): Promise<BulkProcessingRun> => {
  const response = await client.post(`${BASE}/bulk-runs`, request, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return response.data;
};

export const listBatchCompanies = async (params: {
  search?: string;
  corporateStatus?: string;
  coveragePriority?: string;
  serverId?: string;
  channelId?: string;
  daysBack?: number;
  offset?: number;
  limit?: number;
}): Promise<BatchCompanyInventoryResponse> => {
  const response = await client.get(`${BASE}/batch-companies`, {
    params: {
      search: params.search ?? '',
      corporate_status: params.corporateStatus ?? '',
      coverage_priority: params.coveragePriority ?? '',
      server_id: params.serverId || undefined,
      channel_id: params.channelId || undefined,
      days_back: params.daysBack ?? 30,
      offset: params.offset ?? 0,
      limit: params.limit ?? 100,
    },
  });
  return response.data;
};

export const startCompanyBulkProcessing = async (
  request: CompanyBulkProcessingRequest,
  idempotencyKey: string
): Promise<BulkProcessingRun> => {
  const response = await client.post(`${BASE}/company-bulk-runs`, request, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return response.data;
};

export const getBulkProcessingRun = async (runId: string): Promise<BulkProcessingRun> => {
  const response = await client.get(`${BASE}/bulk-runs/${encodeURIComponent(runId)}`);
  return response.data;
};

export const getCompanyState = async (companyId: string): Promise<CompanyStateResponse> => {
  const response = await client.get(`${BASE}/companies/${encodeURIComponent(companyId)}/state`);
  return response.data;
};
